import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getApiCall } from '@/service/httpCall';
import { API_VOICE_CAMPAIGNS_CALL_GROUPS } from '@/service/url';

type CallGroup = { id: string; group_name: string | null; strategy: string | null; dtmf: string | null; agent_count: number };

const CallGroupsViewerModal = ({
  campaignId,
  campaignName,
  onHide,
}: {
  campaignId: string | number | null;
  campaignName?: string | null;
  onHide: () => void;
}) => {
  const [groups, setGroups] = useState<CallGroup[] | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    let alive = true;
    setGroups(null);
    getApiCall(API_VOICE_CAMPAIGNS_CALL_GROUPS.replace(':id', String(campaignId)), {})
      .then((res: unknown) => alive && setGroups(Array.isArray(res) ? (res as CallGroup[]) : []))
      .catch(() => alive && setGroups([]));
    return () => {
      alive = false;
    };
  }, [campaignId]);

  const showDtmf = (groups || []).some((g) => g.dtmf);

  return (
    <Dialog open={Boolean(campaignId)} onOpenChange={(next) => !next && onHide()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Call Groups{campaignName ? ` — ${campaignName}` : ''}</DialogTitle>
        </DialogHeader>
        {groups === null ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading call groups…
          </div>
        ) : groups.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">No call groups linked to this campaign.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-4 font-medium">Call Group</th>
                  <th className="py-1.5 pr-4 font-medium">Ring Strategy</th>
                  {showDtmf && <th className="py-1.5 pr-4 font-medium">DTMF Key</th>}
                  <th className="py-1.5 font-medium">Agents</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} className="border-t border-border/60">
                    <td className="py-1.5 pr-4">{g.group_name || `#${g.id}`}</td>
                    <td className="py-1.5 pr-4">{g.strategy || '—'}</td>
                    {showDtmf && <td className="py-1.5 pr-4">{g.dtmf || '—'}</td>}
                    <td className="py-1.5 tabular-nums">{g.agent_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CallGroupsViewerModal;
